import { useState, useEffect } from 'react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Star,
  Plus,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  User
} from 'lucide-react';
import { procurementService, VendorEvaluation, VendorEvaluationCreate, PerformanceRating } from '../../../src/services/procurement.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { authService } from '../../../src/services/auth.service';
import { userService } from '../../../src/services/user.service';
import type { UserProfile } from '../../../src/services/user.service';

interface VendorEvaluationsProps {
  vendorId: string;
  vendorName: string;
}

interface EvaluationModalProps {
  vendorId: string;
  trigger: React.ReactNode;
  onSuccess: () => void;
}

function EvaluationModal({ vendorId, trigger, onSuccess }: EvaluationModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    evaluation_period_start: '',
    evaluation_period_end: '',
    evaluation_type: 'quarterly',
    overall_rating: 'satisfactory' as PerformanceRating,
    quality_rating: 'satisfactory' as PerformanceRating,
    timeliness_rating: 'satisfactory' as PerformanceRating,
    communication_rating: 'satisfactory' as PerformanceRating,
    cost_effectiveness_rating: 'satisfactory' as PerformanceRating,
    safety_rating: 'satisfactory' as PerformanceRating,
    strengths: '',
    areas_for_improvement: '',
    specific_incidents: '',
    recommendations: '',
    on_time_delivery_percent: '',
    budget_variance_percent: '',
    defect_rate_percent: '',
    evaluation_date: new Date().toISOString().split('T')[0]
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const performanceRatings: { value: PerformanceRating; label: string; color: string }[] = [
    { value: 'excellent', label: 'Excellent', color: 'text-green-600' },
    { value: 'good', label: 'Good', color: 'text-blue-600' },
    { value: 'satisfactory', label: 'Satisfactory', color: 'text-yellow-600' },
    { value: 'needs_improvement', label: 'Needs Improvement', color: 'text-orange-600' },
    { value: 'poor', label: 'Poor', color: 'text-red-600' }
  ];

  // Convert numeric rating (1-5) to PerformanceRating string
  const numberToRating = (num: number): PerformanceRating => {
    const ratingMap: Record<number, PerformanceRating> = {
      5: 'excellent',
      4: 'good',
      3: 'satisfactory',
      2: 'needs_improvement',
      1: 'poor'
    };
    return ratingMap[num] || 'satisfactory';
  };

  // Convert PerformanceRating string to numeric rating (1-5)
  const ratingToNumber = (rating: PerformanceRating): number => {
    const numberMap: Record<PerformanceRating, number> = {
      excellent: 5,
      good: 4,
      satisfactory: 3,
      needs_improvement: 2,
      poor: 1
    };
    return numberMap[rating] || 3;
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.evaluation_period_start) {
      newErrors.evaluation_period_start = 'Start date is required';
    }
    if (!formData.evaluation_period_end) {
      newErrors.evaluation_period_end = 'End date is required';
    }
    if (formData.evaluation_period_start && formData.evaluation_period_end &&
        formData.evaluation_period_start >= formData.evaluation_period_end) {
      newErrors.evaluation_period_end = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in all required fields correctly'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const evaluationData: VendorEvaluationCreate = {
        evaluation_period_start: formData.evaluation_period_start,
        evaluation_period_end: formData.evaluation_period_end,
        evaluation_type: formData.evaluation_type,
        overall_rating: formData.overall_rating,
        overall_score: ratingToNumber(formData.overall_rating),
        quality_rating: formData.quality_rating,
        quality_score: ratingToNumber(formData.quality_rating),
        timeliness_rating: formData.timeliness_rating,
        timeliness_score: ratingToNumber(formData.timeliness_rating),
        communication_rating: formData.communication_rating,
        communication_score: ratingToNumber(formData.communication_rating),
        cost_effectiveness_rating: formData.cost_effectiveness_rating,
        cost_effectiveness_score: ratingToNumber(formData.cost_effectiveness_rating),
        safety_rating: formData.safety_rating,
        safety_score: ratingToNumber(formData.safety_rating),
        strengths: formData.strengths || undefined,
        areas_for_improvement: formData.areas_for_improvement || undefined,
        specific_incidents: formData.specific_incidents || undefined,
        recommendations: formData.recommendations || undefined,
        on_time_delivery_percent: formData.on_time_delivery_percent ? parseFloat(formData.on_time_delivery_percent) : undefined,
        budget_variance_percent: formData.budget_variance_percent ? parseFloat(formData.budget_variance_percent) : undefined,
        defect_rate_percent: formData.defect_rate_percent ? parseFloat(formData.defect_rate_percent) : undefined,
        evaluation_date: formData.evaluation_date
      };

      const userProfile = await authService.getUserProfile();
      await procurementService.createVendorEvaluation(vendorId, evaluationData, userProfile.organizationId);
      showSuccess({
        title: 'Evaluation Submitted',
        message: 'Vendor evaluation has been submitted successfully'
      });

      onSuccess();
    } catch (error: any) {
      console.error('Failed to create evaluation:', error);
      showError({
        title: 'Failed to Submit Evaluation',
        message: error?.message || 'An unexpected error occurred'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const RatingSlider = ({ label, value, onChange }: { label: string; value: number; onChange: (val: number) => void }) => (
    <div className="space-y-2">
      <div className="flex justify-between">
        <Label>{label}</Label>
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${i < value ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}`}
            />
          ))}
          <span className="ml-2 text-sm font-medium">{value}/5</span>
        </div>
      </div>
      <input
        type="range"
        min="1"
        max="5"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
      />
    </div>
  );

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5" />
            Create Vendor Evaluation
          </DialogTitle>
          <DialogDescription>
            Evaluate vendor performance for the specified period
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Period Selection */}
          <div className="space-y-4">
            <h4 className="font-medium">Evaluation Period</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="period-start">Start Date *</Label>
                <Input
                  id="period-start"
                  type="date"
                  value={formData.evaluation_period_start}
                  onChange={(e) => setFormData({...formData, evaluation_period_start: e.target.value})}
                />
                {errors.evaluation_period_start && <p className="text-sm text-destructive">{errors.evaluation_period_start}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="period-end">End Date *</Label>
                <Input
                  id="period-end"
                  type="date"
                  value={formData.evaluation_period_end}
                  onChange={(e) => setFormData({...formData, evaluation_period_end: e.target.value})}
                />
                {errors.evaluation_period_end && <p className="text-sm text-destructive">{errors.evaluation_period_end}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="eval-type">Evaluation Type</Label>
              <Select
                value={formData.evaluation_type}
                onValueChange={(value) => setFormData({...formData, evaluation_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                  <SelectItem value="project_based">Project Based</SelectItem>
                  <SelectItem value="ad_hoc">Ad-hoc</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Overall Rating */}
          <div className="space-y-4">
            <h4 className="font-medium">Overall Performance Rating</h4>
            <Select
              value={formData.overall_rating}
              onValueChange={(value) => setFormData({...formData, overall_rating: value as PerformanceRating})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {performanceRatings.map((rating) => (
                  <SelectItem key={rating.value} value={rating.value}>
                    <span className={rating.color}>{rating.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Detailed Ratings */}
          <div className="space-y-4">
            <h4 className="font-medium">Detailed Ratings (1-5 scale)</h4>
            <RatingSlider
              label="Quality Rating"
              value={ratingToNumber(formData.quality_rating)}
              onChange={(val) => setFormData({...formData, quality_rating: numberToRating(val)})}
            />
            <RatingSlider
              label="Timeliness Rating"
              value={ratingToNumber(formData.timeliness_rating)}
              onChange={(val) => setFormData({...formData, timeliness_rating: numberToRating(val)})}
            />
            <RatingSlider
              label="Communication Rating"
              value={ratingToNumber(formData.communication_rating)}
              onChange={(val) => setFormData({...formData, communication_rating: numberToRating(val)})}
            />
            <RatingSlider
              label="Cost Effectiveness Rating"
              value={ratingToNumber(formData.cost_effectiveness_rating)}
              onChange={(val) => setFormData({...formData, cost_effectiveness_rating: numberToRating(val)})}
            />
            <RatingSlider
              label="Safety Rating"
              value={ratingToNumber(formData.safety_rating)}
              onChange={(val) => setFormData({...formData, safety_rating: numberToRating(val)})}
            />
          </div>

          {/* Performance Metrics */}
          <div className="space-y-4">
            <h4 className="font-medium">Performance Metrics</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="on-time">On-Time Delivery %</Label>
                <Input
                  id="on-time"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.on_time_delivery_percent}
                  onChange={(e) => setFormData({...formData, on_time_delivery_percent: e.target.value})}
                  placeholder="95"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="budget">Budget Variance %</Label>
                <Input
                  id="budget"
                  type="number"
                  value={formData.budget_variance_percent}
                  onChange={(e) => setFormData({...formData, budget_variance_percent: e.target.value})}
                  placeholder="+/- 5"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="defect">Defect Rate %</Label>
                <Input
                  id="defect"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.defect_rate_percent}
                  onChange={(e) => setFormData({...formData, defect_rate_percent: e.target.value})}
                  placeholder="2"
                />
              </div>
            </div>
          </div>

          {/* Detailed Feedback */}
          <div className="space-y-4">
            <h4 className="font-medium">Detailed Feedback</h4>
            <div className="space-y-2">
              <Label htmlFor="strengths">Strengths</Label>
              <Textarea
                id="strengths"
                value={formData.strengths}
                onChange={(e) => setFormData({...formData, strengths: e.target.value})}
                placeholder="What did the vendor do well?"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="improvements">Areas for Improvement</Label>
              <Textarea
                id="improvements"
                value={formData.areas_for_improvement}
                onChange={(e) => setFormData({...formData, areas_for_improvement: e.target.value})}
                placeholder="Where can the vendor improve?"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="incidents">Specific Incidents</Label>
              <Textarea
                id="incidents"
                value={formData.specific_incidents}
                onChange={(e) => setFormData({...formData, specific_incidents: e.target.value})}
                placeholder="Any notable incidents or issues?"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="recommendations">Recommendations</Label>
              <Textarea
                id="recommendations"
                value={formData.recommendations}
                onChange={(e) => setFormData({...formData, recommendations: e.target.value})}
                placeholder="Future recommendations for working with this vendor"
                rows={2}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild><Button variant="outline" disabled={isSubmitting}>Cancel</Button></DialogClose>
          <Button onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Evaluation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VendorEvaluations({ vendorId, vendorName }: VendorEvaluationsProps) {
  const [evaluations, setEvaluations] = useState<VendorEvaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserProfile[]>([]);

  const loadUsers = async () => {
    try {
      const response = await userService.getUsers();
      if (response.success && response.data) {
        const userData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        setUsers(userData);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const fetchEvaluations = async () => {
    try {
      setLoading(true);
      const response = await procurementService.getVendorEvaluations(vendorId);
      const evaluationsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setEvaluations(evaluationsData);
    } catch (error) {
      console.error('Failed to fetch evaluations:', error);
      showError({
        title: 'Failed to Load Evaluations',
        message: 'Unable to load vendor evaluations'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    fetchEvaluations();
  }, [vendorId]);

  const getRatingBadge = (rating: PerformanceRating) => {
    switch (rating) {
      case 'excellent':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Excellent</Badge>;
      case 'good':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Good</Badge>;
      case 'satisfactory':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Satisfactory</Badge>;
      case 'needs_improvement':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200">Needs Improvement</Badge>;
      case 'poor':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Poor</Badge>;
      default:
        return <Badge variant="outline">{rating}</Badge>;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getUserName = (userId: string | undefined | null) => {
    if (!userId) return 'Unknown';
    const user = users.find(u => u.user_id === userId);
    if (!user) return userId;
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.first_name || user.last_name || user.full_name || user.email || userId;
  };

  const calculateAverageRating = () => {
    if (evaluations.length === 0) return 0;
    const ratingMap: Record<PerformanceRating, number> = {
      excellent: 5,
      good: 4,
      satisfactory: 3,
      needs_improvement: 2,
      poor: 1
    };
    const sum = evaluations.reduce((acc, evaluation) => acc + ratingMap[evaluation.overall_rating], 0);
    return (sum / evaluations.length).toFixed(1);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading evaluations...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Performance Evaluations</h3>
          <p className="text-sm text-muted-foreground">Track and evaluate {vendorName} performance</p>
        </div>
        <EvaluationModal
          vendorId={vendorId}
          onSuccess={fetchEvaluations}
          trigger={
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Evaluation
            </Button>
          }
        />
      </div>

      {evaluations.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
                <span className="text-2xl font-bold">{calculateAverageRating()}</span>
                <span className="text-muted-foreground">/5.0</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Total Evaluations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{evaluations.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Latest Evaluation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm">
                {formatDate(evaluations[0].evaluation_date)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {evaluations.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <Star className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No evaluations yet</h3>
          <p className="text-muted-foreground mb-4">Start tracking vendor performance by creating evaluations</p>
          <EvaluationModal
            vendorId={vendorId}
            onSuccess={fetchEvaluations}
            trigger={
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create First Evaluation
              </Button>
            }
          />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead>Overall Rating</TableHead>
              <TableHead>Scores</TableHead>
              <TableHead>Metrics</TableHead>
              <TableHead>Evaluated By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {evaluations.map((evaluation) => (
              <TableRow key={evaluation.evaluation_id}>
                <TableCell>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      {formatDate(evaluation.evaluation_period_start)} - {formatDate(evaluation.evaluation_period_end)}
                    </div>
                    <Badge variant="outline" className="text-xs">{evaluation.evaluation_type}</Badge>
                  </div>
                </TableCell>
                <TableCell>
                  {getRatingBadge(evaluation.overall_rating)}
                </TableCell>
                <TableCell>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-20">Quality:</span>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-3 w-3 ${i < (evaluation.quality_score || 0) ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-20">Timeliness:</span>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-3 w-3 ${i < (evaluation.timeliness_score || 0) ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1 text-sm">
                    {evaluation.on_time_delivery_percent !== null && (
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-3 w-3 text-green-600" />
                        <span>On-time: {evaluation.on_time_delivery_percent}%</span>
                      </div>
                    )}
                    {evaluation.defect_rate_percent !== null && (
                      <div className="flex items-center gap-2">
                        <TrendingDown className="h-3 w-3 text-red-600" />
                        <span>Defects: {evaluation.defect_rate_percent}%</span>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-3 w-3 text-muted-foreground" />
                    <div>
                      <p>{getUserName(evaluation.evaluated_by)}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(evaluation.evaluation_date)}</p>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
